import React, { useEffect, useState } from 'react';
import Slider from 'react-slick';
import Loader from '../Loader/Loader';
import ArrowRightIcon from '../Icons/Action/ArrowRightIcon';
import { getPartners } from '@/services/strapi/strapiCategoryService';
import type { Partner } from '@/types/partner';
import PartnerItem from '../Partners/PartnerItem';
import { useSelectedCategories } from '@/redux/selectors/categorySelector';
import PartnerModal from '../Modal/PartnerModal';
import useBodyScrollLock from '@/hooks/useBodyScrollLock';
import { T } from '@tolgee/react';
import { transformPartnerData } from '@/utils/modalUtils';
import LimitModal from '@/components/Modal/LimitModal';
import { useSelectedLanguage } from '@/redux/selectors/languageSelector';

const NextArrow = ({ onClick, isVisible }: { onClick?: () => void; isVisible: boolean }) => (
  <div
    className={`absolute right-6 z-10 top-0 bottom-0 m-auto cursor-pointer rounded-full border border-white bg-white/30 backdrop-blur-[10px] w-[60px] h-[60px] flex items-center justify-center transition-opacity duration-300 ${
      isVisible ? 'opacity-100' : 'opacity-0 pointer-events-none'
    }`}
    onClick={onClick}
  >
    <ArrowRightIcon width={20} height={20} />
  </div>
);

const PrevArrow = ({ onClick, currentSlide }: { onClick?: () => void; currentSlide: number }) =>
  currentSlide > 0 ? (
    <div
      className="absolute left-6 z-10 top-0 bottom-0 m-auto cursor-pointer rounded-full border border-white bg-white/30 backdrop-blur-[10px] w-[60px] h-[60px] flex items-center justify-center"
      onClick={onClick}
    >
      <ArrowRightIcon width={20} height={20} className="transform rotate-180" />
    </div>
  ) : null;

const PartnerSlider: React.FC = () => {
  const [partners, setPartners] = useState<Partner[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const selectedCategories = useSelectedCategories();
  const locale = useSelectedLanguage();
  const [usageLimitExceeded, setUsageLimitExceeded] = useState<boolean>(false);

  const handleIsLimitReached = () => {
    setSelectedPartner(null);
    setModalOpen(false);
    setUsageLimitExceeded(true);
  };

  const closeLimitModal = () => {
    setUsageLimitExceeded(false);
  };

  const [selectedPartner, setSelectedPartner] = useState<Partner | null>(null);
  const [isModalOpen, setModalOpen] = useState(false);

  useBodyScrollLock(isModalOpen);

  const openModal = (partner: Partner) => {
    setSelectedPartner(partner);
    setModalOpen(true);
  };

  const closeModal = () => {
    setSelectedPartner(null);
    setModalOpen(false);
  };

  useEffect(() => {
    const fetchPartners = async () => {
      setLoading(true);
      const categorySlugs = selectedCategories.filter((category) => category !== 'all');
      try {
        const response = await getPartners(5, 0, ['updatedAt:desc'], categorySlugs, locale);
        setPartners(response.data);
      } catch (error) {
        console.error('Error fetching partners:', error);
        setError(error as Error);
      } finally {
        setLoading(false);
      }
    };

    void fetchPartners();
  }, [locale]);

  const [currentSlide, setCurrentSlide] = useState(0);
  const slideCount = partners.length;
  const settings = {
    dots: false,
    infinite: false,
    speed: 500,
    slidesToShow: 3.06,
    slidesToScroll: 1,
    centerMode: false,
    nextArrow: <NextArrow isVisible={currentSlide < slideCount - 2} />,
    prevArrow: <PrevArrow currentSlide={currentSlide} />,
    beforeChange: (current: number, next: number) => setCurrentSlide(Math.round(next)),
    responsive: [
      {
        breakpoint: 1440,
        settings: {
          slidesToShow: 3.2,
          slidesToScroll: 1,
        },
      },
      {
        breakpoint: 1280,
        settings: {
          slidesToShow: 2.2,
          slidesToScroll: 1,
        },
      },
      {
        breakpoint: 960,
        settings: {
          slidesToShow: 1.2,
          slidesToScroll: 1,
        },
      },
    ],
  };

  if (loading) return <Loader position={'absolute'} />;
  if (error) return <div>Error loading partners</div>;
  if (!partners.length) return null;
  return (
    <div className="relative p-5 md:p-12 pr-0 mb-3 w-full">
      <div className="relative">
        <h2 className="text-4xl">
          <T>home.filtered.apps_courses_section.title.label</T>
        </h2>
      </div>
      <Slider {...settings} className="mt-4">
        {partners.map((partner) => (
          <div key={partner.id} onClick={() => openModal(partner)}>
            <PartnerItem
              name={partner.attributes.name}
              logo={partner.attributes.logo?.data?.attributes?.url || ''}
              backgroundImage={partner.attributes.cover?.data?.attributes?.url || ''}
              title={partner.attributes.title || ''}
              slider={true}
            />
          </div>
        ))}
      </Slider>
      {usageLimitExceeded && <LimitModal isOpen={usageLimitExceeded} onClose={closeLimitModal} />}
      {selectedPartner && (
        <PartnerModal
          partner={transformPartnerData(selectedPartner as Partner)}
          isOpen={isModalOpen}
          onClose={closeModal}
          handleIsLimitReached={handleIsLimitReached}
        />
      )}
    </div>
  );
};

export default PartnerSlider;
